/**
 * `<CurrentVersionIndicator>` — Inline indicator for the conversation's
 * current version pointer (ADR 0019).
 *
 * conversation-model-versioning Phase 5 Session 4 (2026-05-17).
 *
 * ADR 0019 — SHAPE/LABEL/PLACEMENT:
 * - Shape:     `<Chip>` primitive (inline pill) — distinct from the
 *              `<AcceptedUnderwritingBadge>` which is a plain `<div>` badge.
 * - Label:     `CURRENT · <label>` — different text from the accepted badge.
 * - Placement: INLINE with the version list area in the VariablesPanel header
 *              (above the AcceptUnderwritingButton cluster) — different screen
 *              region from the accepted badge which sits adjacent to the
 *              Accept control.
 *
 * Neutral tokens (`text-text` / `border-panel-border`) — NOT accent (accent
 * is reserved for the higher-stakes accepted-for-underwriting signal per
 * ADR 0019 Consequences).
 *
 * Resolves `currentVersionId` → label via the versions list. Renders null
 * when `currentVersionId` is null/undefined (no version exists yet) OR when
 * the versions list hasn't loaded yet (safe degradation — no flicker).
 *
 * Data source: `useConversations()` list → find by conversationId →
 * `.currentVersionId` (the Session-2-widened `ConversationItem`). The detail
 * endpoint (`useConversation`) does NOT expose this pointer — Session-3
 * established precedent. `useConversationVersions` resolves the label.
 */
import clsx from 'clsx';
import { useConversationVersions } from '@/hooks/use-conversation-versions';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CurrentVersionIndicatorProps {
  /** The id of the version currently pointed to by `current_version_id`. */
  currentVersionId: string | null | undefined;
  /** Active conversation id — used to fetch the versions list for label resolution. */
  conversationId: string | null | undefined;
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CurrentVersionIndicator({
  currentVersionId,
  conversationId,
  className,
}: CurrentVersionIndicatorProps) {
  const versionsQuery = useConversationVersions(
    currentVersionId ? conversationId : null
  );

  if (!currentVersionId) return null;

  // Resolve label from the versions list.
  const label =
    versionsQuery.data?.find((v) => v.id === currentVersionId)?.label ?? null;

  // Don't render until the label is resolved — avoids showing an id.
  if (!label) return null;

  return (
    <span
      data-testid="current-version-indicator"
      className={clsx(
        // Shape: pill/chip — inline-flex with border, 2px radius, mono text.
        // Structurally distinct from AcceptedUnderwritingBadge (different element
        // shape, label prefix, and screen placement — ADR 0019).
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] border',
        'font-mono text-[10px] font-semibold tracking-[0.5px] uppercase',
        // Neutral tokens — NOT accent (accent reserved for the accepted badge).
        'text-text border-panel-border bg-panel-bg',
        className
      )}
    >
      {/* Leading dot — same visual language as BrowseVersionsModal CURRENT row prefix */}
      <span
        className="w-1.5 h-1.5 rounded-full bg-text-muted shrink-0"
        aria-hidden="true"
      />
      {/* Label: "CURRENT · <label>" — distinct from "ACCEPTED FOR UNDERWRITING · <label>" */}
      <span>CURRENT · {label}</span>
    </span>
  );
}
